// A saved segment's state (UI-4): for a static one, its build as the
// `segments run` loop moves it — queued, building and for how long, ready
// with its size, stale after an edit, or failed with the engine's own
// reason; for a dynamic one, its last count and when. Beside it the
// campaigns that use it, and the log.

import { Link } from "react-router-dom";
import { HammerIcon, RefreshCwIcon } from "lucide-react";
import type { SegmentDetail, SegmentUse } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/emc/StatusPill";
import { Callout } from "@/components/emc/states";
import { count, minute } from "@/components/emc/format";
import { statusLook } from "@/components/emc/status";
import { EngineError } from "@/pages/segments/EngineError";
import { ago, elapsed } from "@/pages/segments/format";

interface Props {
  segment: SegmentDetail;
  uses: SegmentUse[];
  /** Ticks while a build runs, so the elapsed time moves. */
  now: number;
  busy: boolean;
  /** Segment names by id, for a reason that names another segment. */
  names?: Map<string, string>;
  onBuild: () => void;
  onCount: () => void;
}

function requestedAt(segment: SegmentDetail): string | null {
  return segment.events.find((event) => event.to_state === "requested")?.occurred_at ?? null;
}

function BuildState({ segment, now, busy, names, onBuild }: Omit<Props, "uses" | "onCount">) {
  const members = count(segment.member_count);
  switch (segment.state) {
    case "defined":
      return (
        <div className="flex flex-col gap-3">
          <p className="text-muted-foreground">
            Még nincs felépítve. Az építés a háttérben fut, egy egész ország tagsága nagyjából egy perc.
          </p>
          <div>
            <Button type="button" disabled={busy} onClick={onBuild}>
              <HammerIcon />
              Felépítés
            </Button>
          </div>
        </div>
      );
    case "requested":
      return (
        <Callout tone="warn">
          Sorban áll — a háttérfolyamat hamarosan felveszi (kérve {ago(requestedAt(segment), now)}). Ha percekig itt
          marad, a szegmens-hurok (<code>segments run</code>) nem fut.
        </Callout>
      );
    case "materialising":
      return (
        <Callout tone="warn">
          Épül — <span className="tabular-nums">{elapsed(segment.materialising_since, now)}</span> óta. Egy egész
          ország tagsága nagyjából egy perc; közben a kampányok a korábbi tagságot látják.
        </Callout>
      );
    case "ready":
      return (
        <div className="flex flex-col gap-3">
          <p>
            <span className="text-section font-semibold tabular-nums">{members}</span>{" "}
            <span className="text-muted-foreground">tag · felépítve {ago(segment.materialised_at, now)}</span>
          </p>
          <div>
            <Button variant="outline" type="button" disabled={busy} onClick={onBuild}>
              <RefreshCwIcon />
              Újraépítés
            </Button>
          </div>
        </div>
      );
    case "stale":
      return (
        <div className="flex flex-col gap-3">
          <Callout tone="warn">
            Elavult: a feltételek változtak, a tagság ({members} fő) még a korábbiaké. Kampány nem indulhat vele, amíg
            újra nem épül.
          </Callout>
          <div>
            <Button type="button" disabled={busy} onClick={onBuild}>
              <HammerIcon />
              Újraépítés most
            </Button>
          </div>
        </div>
      );
    case "failed":
      return (
        <div className="flex flex-col gap-3">
          <EngineError
            title="Az építés nem sikerült"
            message={segment.failure ?? "A napló nem mondja meg, miért."}
            names={names}
          />
          <div>
            <Button type="button" disabled={busy} onClick={onBuild}>
              <RefreshCwIcon />
              Újrapróbálás
            </Button>
          </div>
        </div>
      );
    default:
      return <p className="text-muted-foreground">Archiválva — csak olvasható, és nem építhető újra.</p>;
  }
}

export function SegmentStatus({ segment, uses, now, busy, names, onBuild, onCount }: Props) {
  const mine = uses.filter((use) => use.segment_id === segment.id);
  return (
    <section className="flex flex-col gap-4 rounded-card border border-line bg-surface p-4 shadow-emc">
      <div className="flex items-center justify-between gap-3">
        <h2 className="caps-label">{segment.kind === "static" ? "Tagság" : "Darabszám"}</h2>
        <StatusPill domain="segment" state={segment.state} />
      </div>

      {segment.kind === "static" ? (
        <BuildState segment={segment} now={now} busy={busy} names={names} onBuild={onBuild} />
      ) : (
        <div className="flex flex-col gap-3">
          <p>
            <span className="text-section font-semibold tabular-nums">{count(segment.member_count)}</span>{" "}
            <span className="text-muted-foreground">
              {segment.counted_at ? `kontakt · számolva ${ago(segment.counted_at, now)}` : "— még nincs számolva"}
            </span>
          </p>
          <p className="text-label text-muted-foreground">
            Dinamikus: nincs lefagyasztott tagsága. A kampány a küldés pillanatában számolja újra, ugyanezekkel a
            feltételekkel.
          </p>
          {segment.state !== "archived" && (
            <div>
              <Button variant="outline" type="button" disabled={busy} onClick={onCount}>
                <RefreshCwIcon />
                Újraszámolás
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2 border-t border-line pt-3">
        <h3 className="caps-label">Használja</h3>
        {mine.length === 0 ? (
          <p className="text-muted-foreground">Egy kampány sem.</p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {mine.map((use) => (
              <li key={`${use.campaign_id}-${use.role}`} className="flex flex-wrap items-center gap-2">
                {use.group_id !== null ? (
                  <Link to={`/kampanyok/${use.group_id}`}>{use.campaign_name}</Link>
                ) : (
                  <span>{use.campaign_name}</span>
                )}
                <span className="text-label text-muted-foreground">
                  {use.role === "audience" ? "közönség" : "kizárás"}
                </span>
                <StatusPill domain="campaign" state={use.campaign_state} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {segment.events.length > 0 && (
        <details className="border-t border-line pt-3">
          <summary className="cursor-pointer caps-label">Napló ({segment.events.length})</summary>
          <ul className="mt-2 flex flex-col gap-2">
            {segment.events.map((event, index) => {
              const why = (event.detail.error ?? event.detail.reason) as string | undefined;
              return (
                <li key={index} className="flex flex-col gap-0.5 text-label">
                  <span className="text-ink">
                    {event.from_state ? statusLook("segment", event.from_state).label : "—"} →{" "}
                    {event.to_state ? statusLook("segment", event.to_state).label : "—"}
                  </span>
                  <span className="data-mono text-muted-foreground">
                    {minute(event.occurred_at)} · {event.actor}
                  </span>
                  {why && <span className="text-danger">{why}</span>}
                </li>
              );
            })}
          </ul>
        </details>
      )}
    </section>
  );
}
